import { useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const SIGNUP_REFERRAL_STORAGE_KEY = "dental_os_signup_referral_code";

const QUESTIONS = [
  {
    id: "dentists",
    title: "How many dentists work at your clinic?",
    options: [
      { id: "solo", label: "Just me", side: "solo", reason: "one dentist" },
      { id: "clinic", label: "2 or more", side: "clinic", reason: "2 or more dentists" },
    ],
  },
  {
    id: "team",
    title: "How big is your support team?",
    subtitle: "Front desk, assistants, admin.",
    options: [
      { id: "small", label: "1 to 2 people", side: "solo", reason: "a 1 to 2 person support team" },
      { id: "larger", label: "3 or more", side: "clinic", reason: "3 or more support staff" },
    ],
  },
  {
    id: "ortho",
    title: "Do you offer orthodontics?",
    subtitle: "Braces or aligners.",
    options: [
      { id: "no", label: "No", side: "solo", reason: "no orthodontics" },
      { id: "yes", label: "Yes", side: "clinic", reason: "orthodontics" },
    ],
  },
  {
    id: "multiVisit",
    title: "Do you plan treatment across multiple visits?",
    subtitle: "Crowns, bridges, full-mouth work.",
    options: [
      { id: "no", label: "No, mostly single-visit", side: "solo", reason: "mostly single-visit treatment" },
      { id: "yes", label: "Yes", side: "clinic", reason: "multi-visit treatment planning" },
    ],
  },
];

function referralCodeFromLocation(search) {
  const params = new URLSearchParams(search || "");
  return (params.get("ref") || "").trim().toLowerCase();
}

function referralCodeFromSession() {
  try {
    return (window.sessionStorage.getItem(SIGNUP_REFERRAL_STORAGE_KEY) || "").trim().toLowerCase();
  } catch {
    return "";
  }
}

function buildResult(answers) {
  const picked = QUESTIONS
    .map((question) => question.options.find((option) => option.id === answers[question.id]))
    .filter(Boolean);
  const clinicReasons = picked.filter((option) => option.side === "clinic").map((option) => option.reason);
  if (clinicReasons.length > 0) {
    return {
      plan: "clinic",
      reason: `Clinic fits because you selected ${joinReasons(clinicReasons)}.`,
    };
  }
  return {
    plan: "solo",
    reason: "Solo fits because you are one dentist with a small support team and no Clinic-only workflow needs.",
  };
}

function joinReasons(reasons) {
  if (reasons.length <= 1) return reasons[0] || "";
  if (reasons.length === 2) return `${reasons[0]} and ${reasons[1]}`;
  return `${reasons.slice(0, -1).join(", ")}, and ${reasons[reasons.length - 1]}`;
}

function otherPlan(plan) {
  return plan === "solo" ? "clinic" : "solo";
}

function planLabel(plan) {
  return plan === "clinic" ? "Clinic" : "Solo";
}

export default function PlanBuilder() {
  const location = useLocation();
  const activeReferralCode = referralCodeFromLocation(location.search) || referralCodeFromSession();
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const result = useMemo(() => buildResult(answers), [answers]);
  const complete = QUESTIONS.every((question) => answers[question.id]);

  if (activeReferralCode) {
    return <Navigate to={`/ref/${encodeURIComponent(activeReferralCode)}`} replace />;
  }

  if (!started) {
    return (
      <PlanBuilderShell>
        <div className="space-y-8" data-testid="plan-builder-entry">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-700">Dental OS signup</p>
            <h1 className="mt-4 text-3xl font-bold leading-tight text-slate-950">Find the right plan</h1>
            <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
              Answer four quick questions and we will point you to Solo or Clinic.
            </p>
          </div>
          <div className="space-y-3">
            <button
              type="button"
              data-testid="plan-builder-start"
              onClick={() => setStarted(true)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-teal-800"
            >
              Start
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
            <Link
              data-testid="plan-builder-skip"
              to="/signup"
              className="block text-center text-sm font-medium text-slate-500 underline-offset-4 hover:text-teal-700 hover:underline"
            >
              I already know, skip to sign up
            </Link>
          </div>
        </div>
      </PlanBuilderShell>
    );
  }

  if (complete) {
    const alternative = otherPlan(result.plan);
    return (
      <PlanBuilderShell>
        <div className="space-y-6" data-testid="plan-builder-result">
          <ProgressDots step={QUESTIONS.length} />
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-700">Result</p>
            <h1 className="mt-3 text-3xl font-bold leading-tight text-slate-950">{planLabel(result.plan)} plan</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600" data-testid="plan-builder-result-reason">
              {result.reason}
            </p>
          </div>
          <div className="rounded-lg border border-teal-200 bg-teal-50 p-4 text-teal-950">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Check className="h-4 w-4" aria-hidden="true" />
              Recommended tier
            </div>
            <p className="mt-2 text-2xl font-bold">{planLabel(result.plan)}</p>
          </div>
          <div className="space-y-3">
            <Link
              data-testid="plan-builder-continue"
              to={`/signup?plan=${result.plan}`}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-teal-800"
            >
              Continue to sign up
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              data-testid="plan-builder-other-plan"
              to={`/signup?plan=${alternative}`}
              className="block text-center text-sm font-medium text-slate-500 underline-offset-4 hover:text-teal-700 hover:underline"
            >
              View {planLabel(alternative)} instead
            </Link>
          </div>
        </div>
      </PlanBuilderShell>
    );
  }

  const question = QUESTIONS[step];
  const currentAnswer = answers[question.id];

  return (
    <PlanBuilderShell>
      <div className="space-y-6" data-testid={`plan-builder-question-${question.id}`}>
        <ProgressDots step={step} />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-700">
            Question {step + 1} of {QUESTIONS.length}
          </p>
          <h1 className="mt-3 text-2xl font-bold leading-tight text-slate-950">{question.title}</h1>
          {question.subtitle ? <p className="mt-2 text-sm leading-6 text-slate-500">{question.subtitle}</p> : null}
        </div>
        <div className="space-y-3" role="radiogroup" aria-label={question.title}>
          {question.options.map((option) => {
            const active = currentAnswer === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`plan-builder-option-${question.id}-${option.id}`}
                onClick={() => {
                  setAnswers((current) => ({ ...current, [question.id]: option.id }));
                  setStep((current) => Math.min(current + 1, QUESTIONS.length - 1));
                }}
                className={`flex w-full rounded-lg border px-4 py-3 text-left text-sm transition ${
                  active
                    ? "border-teal-700 bg-teal-50 text-teal-950"
                    : "border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:bg-teal-50/50"
                }`}
              >
                <span className="font-medium">{option.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between pt-1 text-sm">
          <button
            type="button"
            data-testid="plan-builder-back"
            disabled={step === 0}
            onClick={() => setStep((current) => Math.max(current - 1, 0))}
            className="inline-flex items-center gap-1 font-medium text-slate-500 transition hover:text-teal-700 disabled:pointer-events-none disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>
          <span className="text-xs font-medium text-slate-400">{step + 1} of {QUESTIONS.length}</span>
        </div>
      </div>
    </PlanBuilderShell>
  );
}

function ProgressDots({ step }) {
  return (
    <div className="flex items-center gap-2" aria-label="Plan builder progress" data-testid="plan-builder-progress">
      {QUESTIONS.map((question, index) => (
        <span
          key={question.id}
          data-testid={`plan-builder-progress-dot-${index + 1}`}
          className={`h-2.5 w-2.5 rounded-full ${index <= step ? "bg-teal-700" : "bg-slate-200"}`}
        />
      ))}
    </div>
  );
}

function PlanBuilderShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-10 text-slate-900">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl items-center justify-center">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}
